import { useState } from "react";
import { Link } from "react-router-dom";
import { Stamp, Search, Plus, Trash2, ExternalLink, ShieldCheck, Loader2, Users, Globe2, UserRound } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useTravelers } from "./useTravelers";

/* Visa & Entry Links — search the official country directory; add your own link when not found. */
const inp = "border border-zinc-300 px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-primary bg-white";

const SHARE_STATUS = {
  pending: ["Pending review", "bg-amber-100 text-amber-800"],
  approved: ["Shared with community", "bg-emerald-100 text-emerald-700"],
  rejected: ["Not approved", "bg-rose-100 text-rose-700"],
};

const ShareStatusPill = ({ l }) => {
  if (!l.shared || !SHARE_STATUS[l.share_status]) return null;
  const [label, cls] = SHARE_STATUS[l.share_status];
  return <span className={`text-[10px] font-700 uppercase px-1.5 py-0.5 ${cls}`} data-testid={`visa-links-share-status-${l.id}`}>{label}</span>;
};

export default function VisaLinks() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ country: "", title: "", url: "", date_posted: "", notes: "", share: false, traveler_name: "" });
  const travelers = useTravelers(adding);

  const search = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) { toast.error("Type at least 2 characters"); return; }
    setBusy(true);
    try { const { data } = await api.get("/trip-visas/links/search", { params: { q } }); setRes(data); }
    catch { toast.error("Search failed"); }
    setBusy(false);
  };

  const save = async () => {
    try {
      await api.post("/trip-visas/links", f);
      toast.success(f.share ? "Link saved — it will appear for other travellers once a moderator approves it" : "Link saved to your Travel Hub");
      setAdding(false);
      setF({ country: "", title: "", url: "", date_posted: "", notes: "", share: false, traveler_name: "" });
      search();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
  };

  const toggleShare = async (l) => {
    try {
      await api.post("/trip-visas/links", { ...l, share: !l.shared });
      toast.success(!l.shared ? "Submitted for community sharing — a moderator will review it shortly" : "No longer shared with the community");
      search();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not update"); }
  };

  const remove = async (id) => { await api.delete(`/trip-visas/links/${id}`); toast.success("Removed"); search(); };

  return (
    <section className="mb-10" data-testid="visa-links">
      <h2 className="font-heading font-900 text-[#34A853] text-2xl flex items-center gap-2 mb-2"><Stamp className="w-6 h-6" /> Visa &amp; Entry Links</h2>
      <p className="text-sm text-muted-foreground mb-3">Search official government visa &amp; entry-card websites for any country — or save your own links.
        {" "}<Link to="/visas" className="text-primary hover:underline">Browse the full directory →</Link></p>
      <form onSubmit={search} className="flex gap-2 mb-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a country (e.g. India, Japan, Brazil)…"
          data-testid="visa-links-search-input" className={`${inp} flex-1`} />
        <button type="submit" disabled={busy} data-testid="visa-links-search-btn"
          className="px-4 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
        </button>
      </form>

      {res && (
        <div className="space-y-2" data-testid="visa-links-results">
          {res.official.map((c) => (
            <div key={c.iso2} className="border border-emerald-200 bg-emerald-50/40 p-3" data-testid={`visa-links-official-${c.iso2}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <b>{c.name}</b>
                <span className="text-[10px] font-700 uppercase tracking-wide bg-emerald-600 text-white px-1.5 py-0.5">Official</span>
                {c.last_verified && <span className="text-xs text-muted-foreground">verified {c.last_verified}</span>}
              </div>
              <div className="flex flex-wrap gap-3 mt-1.5 text-xs">
                {Object.entries(c.links || {}).map(([k, v]) => typeof v === "string" && v.startsWith("http") && (
                  <a key={k} href={v} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline capitalize">{k.replace(/_/g, " ")} <ExternalLink className="w-3 h-3" /></a>
                ))}
              </div>
            </div>
          ))}
          {res.mine.map((l) => (
            <div key={l.id} className="border border-zinc-300 bg-white p-3" data-testid={`visa-links-mine-${l.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <b>{l.country}</b> — <a href={l.url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">{l.title} <ExternalLink className="w-3 h-3" /></a>
                <span className="text-[10px] font-700 uppercase bg-zinc-200 text-zinc-600 px-1.5 py-0.5">My link</span>
                {l.traveler_name && <span className="inline-flex items-center gap-1 text-[10px] font-700 bg-blue-50 text-blue-700 px-1.5 py-0.5" data-testid={`visa-links-traveler-${l.id}`}><UserRound className="w-3 h-3" /> {l.traveler_name}</span>}
                <ShareStatusPill l={l} />
                <span className="text-xs text-muted-foreground">{l.date_posted}</span>
                <button onClick={() => toggleShare(l)} data-testid={`visa-links-share-${l.id}`}
                  title={l.shared ? "Stop sharing with the community" : "Share this link with other travellers (moderator reviewed)"}
                  className={`ml-auto inline-flex items-center gap-1 text-[11px] font-600 px-1.5 py-0.5 border transition-colors ${l.shared ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-zinc-300 text-zinc-600 hover:border-emerald-400 hover:text-emerald-700"}`}>
                  <Globe2 className="w-3 h-3" /> {l.shared ? "Unshare" : "Share"}
                </button>
                <button onClick={() => remove(l.id)} data-testid={`visa-links-delete-${l.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              {l.notes && <div className="text-xs text-muted-foreground mt-1">{l.notes}</div>}
            </div>
          ))}
          {(res.community || []).map((l) => (
            <div key={l.id} className="border border-sky-200 bg-sky-50/40 p-3" data-testid={`visa-links-community-${l.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Users className="w-4 h-4 text-sky-600" />
                <b>{l.country}</b> — <a href={l.url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">{l.title} <ExternalLink className="w-3 h-3" /></a>
                <span className="text-[10px] font-700 uppercase bg-sky-600 text-white px-1.5 py-0.5">Community</span>
                <span className="text-xs text-muted-foreground">{l.date_posted}</span>
              </div>
              {l.notes && <div className="text-xs text-muted-foreground mt-1">{l.notes}</div>}
            </div>
          ))}
          {res.official.length === 0 && res.mine.length === 0 && (res.community || []).length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="visa-links-empty">Nothing found for "{q}". Know the official link? Add it below so it's always at hand.</p>
          )}
        </div>
      )}

      <div className="mt-3">
        {!adding ? (
          <button onClick={() => { setAdding(true); setF({ ...f, country: q.trim(), date_posted: new Date().toISOString().slice(0, 10) }); }}
            data-testid="visa-links-add-btn"
            className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-xs hover:border-primary text-primary">
            <Plus className="w-3.5 h-3.5" /> Add a visa / entry link
          </button>
        ) : (
          <div className="border border-violet-300 bg-white p-3 grid sm:grid-cols-2 gap-2" data-testid="visa-links-form">
            <input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} placeholder="Country name *" data-testid="visa-links-f-country" className={inp} />
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title for the link *" data-testid="visa-links-f-title" className={inp} />
            <input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://…" data-testid="visa-links-f-url" className={`${inp} sm:col-span-2`} />
            <label className="text-xs text-muted-foreground">Date posted<input type="date" value={f.date_posted} onChange={(e) => setF({ ...f, date_posted: e.target.value })} data-testid="visa-links-f-date" className={`${inp} w-full`} /></label>
            <label className="text-xs text-muted-foreground">For traveller (optional)
              <select value={f.traveler_name} onChange={(e) => setF({ ...f, traveler_name: e.target.value })} data-testid="visa-links-f-traveler" className={`${inp} w-full`}>
                <option value="">— Whole household —</option>
                {travelers.map((t) => <option key={t.id} value={t.name}>{t.name}{t.relation ? ` (${t.relation})` : ""}</option>)}
              </select>
            </label>
            <input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Notes (optional)" data-testid="visa-links-f-notes" className={`${inp} sm:col-span-2`} />
            <label className="sm:col-span-2 inline-flex items-center gap-2 text-xs text-zinc-700 cursor-pointer" data-testid="visa-links-f-share">
              <input type="checkbox" checked={!!f.share} onChange={(e) => setF({ ...f, share: e.target.checked })} />
              <Users className="w-3.5 h-3.5 text-sky-600" /> Share with the community — other travellers will see this link after a quick moderator review
            </label>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button onClick={() => setAdding(false)} className="px-3 py-1.5 border border-zinc-300 text-xs">Cancel</button>
              <button onClick={save} data-testid="visa-links-f-save" className="px-3 py-1.5 bg-primary text-white text-xs font-600">Save link</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
